import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "@chakra-ui/react";
import { usePreflightInviteToken } from "./hooks/usePreflightInviteToken";
import { EmailConfirmationStep } from "./EmailConfirmationStep";
import { VerificationCodeStep } from "./VerificationCodeStep";
import { CompleteRegistrationStep } from "./CompleteRegistrationStep";
import { ExpiredInvitePage } from "./ExpiredInvitePage";
import { InviteErrorPage, type InviteErrorCode } from "./InviteErrorPage";
import type { RegistrationPrefill, UserListItem } from "../types/userInvite";
import AuthContainer from "../components/AuthContainer";
import { useAuthContext } from "../context/AuthContextProvider";

type RegistrationStep =
  | "PREFLIGHT"
  | "CONFIRM_EMAIL"
  | "VERIFY_CODE"
  | "COMPLETE_REGISTRATION"
  | "SUCCESS";

// Read the token from the URL at module-eval time. We deliberately do NOT
// strip it from the URL — the token is single-use, expires server-side, and
// is consumed when the registration completes. Keeping it visible means a
// refresh at ANY step (CONFIRM_EMAIL / VERIFY_CODE / COMPLETE_REGISTRATION)
// re-runs the idempotent, email-free preflight rather than dropping the
// user onto an "invite expired" page. Preflight returns the masked email
// only — no PII leakage via Referer/analytics beyond what's already in the
// invite link the user just clicked.
const readToken = (): string | null => {
  const params = new URLSearchParams(window.location.search);
  return params.get("token");
};

// Captured once at component creation time (not inside render/effect).
const INITIAL_TOKEN = readToken();

interface RegistrationPageProps {
  path?: string;
}

/**
 * Replace {INVITEE_NAME} / {COMPANY_NAME} placeholders in the welcome
 * header with the values returned from preflight. Falls back to neutral
 * copy when the values aren't available yet (preflight in flight) so the
 * placeholder text never reaches the user.
 */
const buildWelcomeText = (
  inviteeName: string | null,
  companyName: string | null
): string => {
  const name = (inviteeName || "").trim() || "Someone";
  const company = (companyName || "").trim() || "their organisation";
  return `${name}, you have been invited to join ${company} on Procuracon`;
};

export const RegistrationPage: React.FC<RegistrationPageProps> = () => {
  // Registration token stored ONLY in component state — never localStorage,
  // sessionStorage, URL, or Redux.
  const [step, setStep] = useState<RegistrationStep>("PREFLIGHT");
  const [registrationToken, setRegistrationToken] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<RegistrationPrefill | null>(null);
  // Issued-code expiry (ISO-8601) — handed off to VerificationCodeStep so
  // it can render an exact "expires at HH:mm" line instead of vague copy.
  const [verificationExpiresAt, setVerificationExpiresAt] = useState<string | null>(null);
  // Terminal error raised by the issue-code call (after preflight succeeded).
  const [issueErrorCode, setIssueErrorCode] = useState<InviteErrorCode | null>(null);
  // Held only to drive the auto-login on the success step. Cleared the
  // moment login completes (see autoLogin below).
  const [autoLoginCreds, setAutoLoginCreds] = useState<{ email: string; password: string } | null>(null);
  const [autoLoginFailed, setAutoLoginFailed] = useState(false);

  const {
    maskedEmail,
    inviteeName,
    companyName,
    invitedByName,
    invitedByEmail,
    isLoading,
    isExpired,
    errorCode,
    retry: retryPreflight,
  } = usePreflightInviteToken(INITIAL_TOKEN);

  const { loginUser } = useAuthContext();

  const handleCodeSent = (expiresAt: string | null) => {
    setVerificationExpiresAt(expiresAt);
    setStep("VERIFY_CODE");
  };

  const handleIssueTerminalError = (code: string) => {
    setIssueErrorCode(code as InviteErrorCode);
  };

  const handleCodeVerified = (token: string, prefillData: RegistrationPrefill) => {
    setRegistrationToken(token);
    setPrefill(prefillData);
    setStep("COMPLETE_REGISTRATION");
  };

  const handleRegistrationSuccess = (
    _user: UserListItem,
    _accessToken: string,
    credentials?: { email: string; password: string }
  ) => {
    setRegistrationToken(null);
    if (credentials) {
      setAutoLoginCreds(credentials);
    }
    setStep("SUCCESS");
  };

  // Once preflight resolves with a masked email, advance to the
  // user-driven email-confirmation step. The verification code is NOT
  // dispatched until the user explicitly clicks "Send code" there.
  useMemo(() => {
    if (!isLoading && maskedEmail && step === "PREFLIGHT") {
      setStep("CONFIRM_EMAIL");
    }
  }, [isLoading, maskedEmail, step]);

  // Auto-login on SUCCESS: the user just typed their password seconds ago,
  // making them re-enter it on the login page is hostile UX. We re-use the
  // existing cookie-based loginUser flow rather than the bearer token from
  // the register response — that keeps session handling consistent with
  // every other login path. A 2s delay gives the success message a chance
  // to render before the navigation kicks in.
  useEffect(() => {
    if (step !== "SUCCESS" || !autoLoginCreds) {
      return;
    }
    const timer = window.setTimeout(() => {
      loginUser({ email: autoLoginCreds.email, password: autoLoginCreds.password })
        .catch(() => {
          setAutoLoginFailed(true);
        })
        .finally(() => {
          // Drop the password from memory regardless of outcome.
          setAutoLoginCreds(null);
        });
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [step, autoLoginCreds, loginUser]);

  if (!INITIAL_TOKEN || isExpired) {
    return <ExpiredInvitePage />;
  }

  // Terminal error from preflight (e.g. RATE_LIMIT_EXCEEDED, VERIFICATION_LOCKED,
  // PREFLIGHT_FAILED) — render a dedicated error page rather than a blank shell.
  // Same handling for terminal errors raised later by the issue-code call.
  // INVITE_TOKEN_EXPIRED / INVITE_TOKEN_INVALID are handled above via isExpired,
  // so we filter them out here to avoid a double-render.
  const terminalError =
    (errorCode && errorCode !== "INVITE_TOKEN_EXPIRED" && errorCode !== "INVITE_TOKEN_INVALID"
      ? (errorCode as InviteErrorCode)
      : null) ?? issueErrorCode;

  if (terminalError) {
    // Only the preflight error is retriable here — issue-code errors require
    // the user to re-enter the flow, so we don't expose retry for those.
    const onRetry =
      terminalError === "PREFLIGHT_FAILED" && !issueErrorCode ? retryPreflight : undefined;
    return <InviteErrorPage code={terminalError} onRetry={onRetry} />;
  }

  if (step === "SUCCESS") {
    return (
      <AuthContainer additionalHeadingText="">
        <div className="flex items-center justify-center px-4">
          <div className="max-w-md rounded-lg bg-white p-8 text-center shadow-sm border border-gray-200">
            <h1 className="mb-2 text-xl font-semibold text-gray-900">Account created!</h1>
            {autoLoginFailed ? (
              <p className="text-sm text-gray-600">
                We couldn&apos;t sign you in automatically. Please{" "}
                <a href="/login" className="text-blue-600 hover:underline">
                  log in
                </a>
                .
              </p>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-600">Logging you in…</p>
                <div className="flex justify-center">
                  <Spinner size="md" color="blue.500" />
                </div>
              </div>
            )}
          </div>
        </div>
      </AuthContainer>
    );
  }

  const isCompleteStep = step === "COMPLETE_REGISTRATION";
  const welcomeText = isCompleteStep
    ? ""
    : buildWelcomeText(inviteeName, companyName);

  return (
    <AuthContainer
      welcomeOverrideText={welcomeText}
      additionalHeadingText={!isCompleteStep && "Lets get you registered..."}
      logoSizeOverride={isCompleteStep ? "sm" : null}
    >
      {/* data-sentry-mask prevents Sentry Session Replay from capturing sensitive fields */}
      <div
        className="w-full max-w-md rounded-lg bg-white p-8 shadow-sm border border-gray-200"
        data-sentry-mask="true"
        data-testid="registration-container"
      >
        {isLoading ? (
          <div className="flex items-center justify-center">
            <Spinner size="xl" color="blue.500" />
          </div>
        ) : (
          <>
            <div className="mb-6">
              <div className="h-1 rounded-full bg-gray-200">
                <div
                  className="h-1 rounded-full bg-blue-600 transition-all duration-300"
                  style={{
                    width:
                      step === "CONFIRM_EMAIL"
                        ? "25%"
                        : step === "VERIFY_CODE"
                          ? "50%"
                          : isCompleteStep
                            ? "100%"
                            : "10%",
                  }}
                />
              </div>
            </div>

            {step === "CONFIRM_EMAIL" && maskedEmail && INITIAL_TOKEN && (
              <EmailConfirmationStep
                inviteToken={INITIAL_TOKEN}
                maskedEmail={maskedEmail}
                invitedByEmail={invitedByEmail}
                invitedByName={invitedByName}
                onCodeSent={handleCodeSent}
                onTerminalError={handleIssueTerminalError}
              />
            )}

            {step === "VERIFY_CODE" && maskedEmail && INITIAL_TOKEN && (
              <VerificationCodeStep
                inviteToken={INITIAL_TOKEN}
                maskedEmail={maskedEmail}
                verificationExpiresAt={verificationExpiresAt}
                onSuccess={handleCodeVerified}
              />
            )}

            {isCompleteStep && registrationToken && prefill && (
              <CompleteRegistrationStep
                registrationToken={registrationToken}
                prefill={prefill}
                onSuccess={handleRegistrationSuccess}
              />
            )}
          </>
        )}
      </div>
    </AuthContainer>
  );
};
